// docs/114 MT4 — how each thing on the field is drawn: an Oryx frame where the sheets have one
// (a second frame to flicker between while it burns or bubbles, another for it unlit), a tint and a
// small mark to tell kinds that share a frame apart, or pixel-art of its own (`textures.ts`); and
// the light it throws while lit.
import type { ThingKind } from '../../core/sim/types';
import { drawnKey } from './textures';

export interface ThingLook {
  /** Atlas frame (`world_…` or `item_…`), or a drawn texture key. */
  frame: string;
  /** A second frame to alternate with (a brazier's flames, a cauldron's bubbles). */
  frameB?: string;
  /** Frame while unlit, for things that start lit (a torch put out). */
  dark?: string;
  tint?: number;
  /** A mark from `textures.ts` laid over the frame. */
  mark?: string;
  /** Share of a square it fills (item frames are small). */
  scale?: number;
  /** Hangs overhead: drawn high in its square. */
  hangs?: boolean;
  /** Its light while lit: colour and radius in squares. */
  light?: { color: number; radius: number };
}

const FIRE = { color: 0xffa040, radius: 3 };
const CANDLE = { color: 0xffd080, radius: 2 };

export const THING_LOOK: Record<ThingKind, ThingLook> = {
  OilBarrel: { frame: 'world_0268', mark: 'drip' },
  PowderKeg: { frame: 'world_0268', tint: 0x9a8a7a, mark: 'keg' },
  WaterBarrel: { frame: 'world_0269', mark: 'water' },
  AleCask: { frame: 'world_0268', tint: 0xe0c080, mark: 'ale' },
  AcidVat: { frame: 'world_0374', frameB: 'world_0375', tint: 0xb0ffa0, mark: 'acid' },
  BrewCauldron: { frame: 'world_0374', frameB: 'world_0375', tint: 0xd0b0ff, mark: 'brew' },
  HotOilVat: { frame: 'world_0374', frameB: 'world_0375', tint: 0xffc890, mark: 'oil', light: FIRE },
  Brazier: { frame: 'world_0099', frameB: 'world_0100', light: FIRE },
  Campfire: { frame: 'world_0097', frameB: 'world_0098', light: FIRE },
  Torch: { frame: 'item_153', dark: 'item_152', scale: 0.8, light: FIRE },
  Lantern: { frame: 'item_148', scale: 0.7, light: CANDLE },
  Candles: { frame: 'world_0323', frameB: 'world_0324', light: CANDLE },
  Chandelier: { frame: drawnKey('Chandelier'), dark: drawnKey('ChandelierDark'), hangs: true, light: CANDLE },
  Boulder: { frame: 'world_0164' },
  Log: { frame: drawnKey('Log') },
  Crate: { frame: 'world_0435' },
  Haybale: { frame: drawnKey('Haybale') },
  Woodpile: { frame: drawnKey('Woodpile') },
  Bookshelf: { frame: 'world_0317' },
  Banner: { frame: 'world_1546' },
  Beehive: { frame: drawnKey('Beehive') },
  WaspNest: { frame: drawnKey('WaspNest') },
  Puffballs: { frame: 'item_094', scale: 0.8 },
  MarshVent: { frame: drawnKey('MarshVent') },
  SteamVent: { frame: drawnKey('SteamVent') },
  Icicles: { frame: drawnKey('Icicles'), hangs: true },
  IcePillar: { frame: drawnKey('IcePillar') },
  HolyFont: { frame: 'world_0486', tint: 0xd8f0ff },
  Well: { frame: 'world_0548' },
  Trough: { frame: drawnKey('Trough') },
  AlchemyBench: { frame: drawnKey('AlchemyBench') },
  FlourSacks: { frame: 'item_151', tint: 0xf4ecd8, scale: 0.85 },
  MineCart: { frame: drawnKey('MineCart') },
  RottenBeam: { frame: drawnKey('RottenBeam') },
  FireworksCrate: { frame: 'world_0436', mark: 'fireworks' },
  TarBarrel: { frame: 'world_0268', tint: 0x5a5050, mark: 'drip' },
  Bell: { frame: drawnKey('Bell') },
  Gong: { frame: drawnKey('Gong') },
  EggSac: { frame: drawnKey('EggSac') },
  DeadTree: { frame: 'world_0276' },
  Snowman: { frame: drawnKey('Snowman') },
  Scarecrow: { frame: drawnKey('Scarecrow') },
  BoneHeap: { frame: 'world_0094' },
  Tent: { frame: drawnKey('Tent') },
  Cart: { frame: drawnKey('Cart') },
  LightningRod: { frame: drawnKey('LightningRod') },
  BlightFont: { frame: 'world_0486', tint: 0x9a70c0 },
  BrineTub: { frame: 'world_0269', tint: 0xc0d0dc, mark: 'brine' },
  Hearth: { frame: drawnKey('Hearth'), light: FIRE },
  Anvil: { frame: drawnKey('Anvil') },
};

/** Things that shine when lit (their flame or candles), not things that are merely on fire. */
export const LIGHTS: readonly ThingKind[] = ['Brazier', 'Campfire', 'Torch', 'Lantern', 'Candles', 'Chandelier', 'Hearth', 'HotOilVat'];

/** Things that bubble or flicker whether lit or not. */
export const ALWAYS_ANIMATED: readonly ThingKind[] = ['AcidVat', 'BrewCauldron'];

/** Fallback when a frame is missing from the atlases: a drawn shape that still reads. */
export const FALLBACK: Partial<Record<ThingKind, string>> = {
  HolyFont: drawnKey('HolyFont'),
  BlightFont: drawnKey('BlightFont'),
};
